import { BodyText, toast } from "@/components/ui";
import { Field, FieldLabel } from "@/components/ui";
import { Search } from "@/components/ui/Search";
import type { ItemOptionProps } from "@/components/ui/Search";
import { EditableRecordButton } from "@/features/Patient/MedicalRecords/components/EditableRecordButton";
import { SubmitCancelButtons } from "@/features/Patient/MedicalRecords/components/SubmitCancelButtons";
import type { Patient } from "@curai/js-fhir-tools";
import { useState } from "react";
import { useUpdatePronouns } from "../network/usePatientIdentity";

// The most common pronoun sets, shown as quick-pick options. The field is freetext, so providers can enter any others.
const PRONOUNS_OPTIONS: ItemOptionProps[] = [
  { label: "she/her", value: "she/her" },
  { label: "he/him", value: "he/him" },
  { label: "they/them", value: "they/them" },
];

export const PronounsEditor = ({ patient }: { patient: Patient }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [pendingValue, setPendingValue] = useState(patient.pronouns ?? "");

  const { mutateAsync: updatePronouns, isPending } = useUpdatePronouns(
    patient.id,
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updatePronouns({
        pronouns: pendingValue.trim() || null,
      });
      setIsEditing(false);
    } catch {
      toast.error("Failed to update pronouns");
    }
  };

  const handleEditClick = () => {
    setPendingValue(patient.pronouns ?? "");
    setIsEditing(true);
  };

  if (isEditing) {
    return (
      <form
        onSubmit={handleSubmit}
        className="flex relative flex-col gap-2 px-3 py-2 w-full"
      >
        <Field>
          <FieldLabel>Pronouns</FieldLabel>
          <Search
            input={patient.pronouns ?? ""}
            options={PRONOUNS_OPTIONS}
            allowFreeText
            freeTextPrefix="Use"
            shouldFilter={false}
            openOnFocus
            openWithoutInput
            placeholder="Select or type pronouns"
            onInputChange={(val) => setPendingValue(val)}
            onItemSelected={(item: ItemOptionProps) => {
              setPendingValue(String(item.value).trim());
            }}
          />
        </Field>
        <SubmitCancelButtons
          isSaving={isPending}
          onCancel={() => setIsEditing(false)}
        />
      </form>
    );
  }

  return (
    <EditableRecordButton title="Pronouns" onClick={handleEditClick}>
      <BodyText size="sm">
        {patient.pronouns || "No Preferred Pronouns"}
      </BodyText>
    </EditableRecordButton>
  );
};
